import { useQuery, useMutation } from "@tanstack/react-query";
import { PatientAvailablePhysicians, PatientId, PhysicianId } from "../../../messages";
import React from "react";
import { emptyStringOrUndefinedToDefault } from "../../../formatUtils";
import { translate } from "../../../translate";
import { STRINGS } from "../../../strings";

export const useAutoCompletePhysicians = (params: {
  patientId: PatientId;
  query: string;
  setPatientPhysician: (patientId: PatientId, physicianId: PhysicianId) => Promise<void>;
  getAllPhysicians: (patientId: PatientId) => Promise<PatientAvailablePhysicians>;
}) => {
  const allPhysiciansQuery = useQuery({
    queryKey: ["patients", params.patientId, "all-physicians"],
    queryFn: () => params.getAllPhysicians(params.patientId),
    select: (data) =>
      data.physicians.map((p) => ({
        ...p,
        id: String(p.id),
        physicianId: p.id,
        title: `${p.firstName} ${p.lastName}, npi: ${emptyStringOrUndefinedToDefault(
          p.npi,
          translate(STRINGS.NONE)
        )}`,
        searchText: `${p.firstName} ${p.lastName} ${p.npi}`.toLowerCase(),
      })),
  });

  const updatePhysicianMutation = useMutation({
    mutationFn: (physicianId: PhysicianId) => {
      return params.setPatientPhysician(params.patientId, physicianId);
    },
  });

  const predictions = React.useMemo(() => {
    return params.query === ""
      ? allPhysiciansQuery.data ?? []
      : allPhysiciansQuery.data?.filter((p) => p.searchText.includes(params.query.toLowerCase())) ??
          [];
  }, [allPhysiciansQuery.data, params.query]);

  return {
    predictions: predictions,
    showPredictions: true,
    isLoading: allPhysiciansQuery.isLoading || updatePhysicianMutation.isLoading,
    updatePhysician: updatePhysicianMutation.mutate,
  };
};
